import { useEffect, useMemo, useState } from "react";
import { salesApi } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useDayLock } from "../hooks/useDayLock";
import { Button, Field, Input, Modal, Select } from "./ui";
import { PaymentFields } from "./PaymentFields";
import { COPY } from "../utils/copy";
import { emptyProof, withProof } from "../utils/finance";
import { unpaidPlanItems } from "../utils/plan";
import { formatDate, formatMoney, roundMoney } from "../utils/format";

const emptyCollect = { amount: "", month: "", note: "", ...emptyProof() };

const seedCollect = (sale) => {
  const unpaid = unpaidPlanItems(sale?.installmentPlan);
  const nextDue = unpaid[0];
  const suggested = nextDue
    ? Math.min(roundMoney(nextDue.amount - (nextDue.paidAmount || 0)), sale?.remainingAmount || 0)
    : Math.min(sale?.monthlyInstallment || 0, sale?.remainingAmount || 0);
  return {
    ...emptyCollect,
    amount: suggested ? String(suggested) : "",
    month: nextDue ? String(nextDue.month) : "",
  };
};

export function CollectInstallmentModal({ sale, open, onClose, onSaved }) {
  const toast = useToast();
  const { isTodayClosed, reload: reloadDays } = useDayLock();
  const { busy, run } = useAsyncAction();
  const [form, setForm] = useState(emptyCollect);

  useEffect(() => {
    if (open && sale) setForm(seedCollect(sale));
  }, [open, sale]);

  const unpaid = unpaidPlanItems(sale?.installmentPlan);
  const afterPay = useMemo(() => {
    if (!sale) return 0;
    return roundMoney(Math.max((sale.remainingAmount || 0) - roundMoney(form.amount), 0));
  }, [sale, form.amount]);

  const save = async (event) => {
    event.preventDefault();
    if (!sale) return;
    const pay = roundMoney(form.amount);
    if (pay > sale.remainingAmount) {
      toast.error("Amount cannot be more than remaining");
      return;
    }
    const body = withProof({ amount: pay, note: form.note || "" }, form);
    if (form.month) body.month = Number(form.month);
    const result = await run(
      () => salesApi.addInstallment(sale.id, body),
      "Installment saved. Remaining went down."
    );
    if (result) {
      reloadDays();
      onSaved?.();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      title={sale ? `Collect from ${sale.buyerName}` : "Collect installment"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="collect-form" disabled={busy || isTodayClosed || !sale}>
            Save installment
          </Button>
        </>
      }
    >
      {isTodayClosed ? <p className="lock-note">{COPY.dayClosed}</p> : null}
      {sale ? (
        <form id="collect-form" className="form-grid" onSubmit={save}>
          <Field label="Amount" hint={`Maximum ${formatMoney(sale.remainingAmount)}`}>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              max={sale.remainingAmount}
              required
              value={form.amount}
              onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
            />
          </Field>
          <Field label="For month">
            <Select
              value={form.month}
              onChange={(event) => setForm((prev) => ({ ...prev, month: event.target.value }))}
            >
              <option value="">Next due month</option>
              {unpaid.map((item) => (
                <option key={item.month} value={item.month}>
                  Month {item.month} · due {formatDate(item.dueDate)} ·{" "}
                  {formatMoney(roundMoney(item.amount - (item.paidAmount || 0)))} left
                </option>
              ))}
            </Select>
          </Field>
          <PaymentFields form={form} onChange={setForm} />
          <Field label="Note" wide>
            <Input
              value={form.note}
              onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))}
              placeholder="Optional"
            />
          </Field>
          <p className="muted field-wide">
            After this, remaining: <b>{formatMoney(afterPay)}</b>
          </p>
        </form>
      ) : null}
    </Modal>
  );
}
